// The assistant panel (spec 2026-10-21-24 §12): a right sheet holding the
// person's threads, the open thread's messages and the composer. The open
// state and the current thread live in localStorage, so the panel follows the
// person across pages.
import { useEffect, useMemo, useRef, useState } from 'react'
import type { KeyboardEvent } from 'react'
import { useParams, useRouterState } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { mdiArchiveOutline, mdiFormatListBulleted, mdiPlus, mdiSend } from '@mdi/js'
import type { AssistantMessage as Message, AssistantStatus } from '../../api/schema'
import { AppIcon } from '../ui/icon'
import { Button } from '../ui/button'
import { Textarea } from '../ui/textarea'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../ui/sheet'
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../ui/alert-dialog'
import { useCanOpenRunDetails } from '../agent-runs/can-open-run'
import { AssistantLiveTurn, AssistantMessage } from './AssistantMessage'
import {
    closeThread,
    createThread,
    postMessage,
    turnInProgress,
    undoAnswer,
    useAssistantThread,
    useAssistantThreadLive,
    useAssistantThreads,
    useInvalidateAssistant,
    type AssistantError,
} from './assistant-api'
import { pageContext } from './page-context'

/** The error codes the panel names; anything else reads as generic. */
export const KNOWN_ERRORS = [
    'ASSISTANT_RUN_ACTIVE',
    'ASSISTANT_THREAD_CLOSED',
    'AGENT_PROVIDER_NOT_ALLOWED',
    'FEATURE_DISABLED',
    'ASSISTANT_TURN_IN_PROGRESS',
    'ASSISTANT_ALREADY_UNDONE',
] as const

/** spendCapped: a per-token pair at today's cap, from the list's status block. */
export function spendCapped(status: AssistantStatus | undefined): boolean {
    return !!status && status.billing === 'per_token' && status.cap_reached
}

const storageKey = (org: string, what: 'open' | 'thread') => `assistant:${what}:${org}`

export function readStored(org: string, what: 'open' | 'thread'): string | null {
    try {
        return localStorage.getItem(storageKey(org, what))
    } catch {
        return null
    }
}

export function writeStored(org: string, what: 'open' | 'thread', value: string | null) {
    try {
        if (value === null) localStorage.removeItem(storageKey(org, what))
        else localStorage.setItem(storageKey(org, what), value)
    } catch {
        // private window: the panel just forgets
    }
}

/** AssistantErrorNotice names an error code, the spend cap apart. */
export function AssistantErrorNotice({ error, capped, onOpenThread }: {
    error?: AssistantError
    capped?: boolean
    onOpenThread?: (uid: string) => void
}) {
    const { t } = useTranslation('assistant')
    if (capped || error?.code === 'AGENT_DAILY_CAP_REACHED') {
        return (
            <div className="rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-xs" data-testid="assistant-spend-capped">
                {t('spend_capped')}
            </div>
        )
    }
    if (!error) return null
    const code = (KNOWN_ERRORS as readonly string[]).includes(error.code ?? '') ? error.code : 'generic'
    return (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs" data-testid="assistant-error">
            {t(`errors.${code}`)}
            {error.code === 'ASSISTANT_RUN_ACTIVE' && error.thread_uid && onOpenThread && (
                <button
                    type="button"
                    className="ml-1 underline"
                    onClick={() => onOpenThread(error.thread_uid as string)}
                    data-testid="assistant-error-open-thread"
                >
                    {t('errors.ASSISTANT_RUN_ACTIVE_link')}
                </button>
            )}
        </div>
    )
}

export function AssistantPanel({ org, open, onOpenChange }: { org: string; open: boolean; onOpenChange: (open: boolean) => void }) {
    const { t } = useTranslation('assistant')
    const params = useParams({ strict: false }) as Record<string, unknown>
    const pathname = useRouterState({ select: (s) => s.location.pathname })
    const context = useMemo(() => pageContext(pathname, params), [pathname, params])
    const canOpenRun = useCanOpenRunDetails()
    const invalidate = useInvalidateAssistant(org)

    const [threadUid, setThreadUidState] = useState<string | null>(() => readStored(org, 'thread'))
    const [showList, setShowList] = useState(false)
    const [tab, setTab] = useState<'open' | 'closed'>('open')
    const [text, setText] = useState('')
    const [error, setError] = useState<AssistantError | undefined>()
    const [sending, setSending] = useState(false)
    const [undoing, setUndoing] = useState(false)
    const [confirmClose, setConfirmClose] = useState(false)
    const bottom = useRef<HTMLDivElement>(null)

    const setThreadUid = (uid: string | null) => {
        setThreadUidState(uid)
        writeStored(org, 'thread', uid)
        setError(undefined)
    }

    const threads = useAssistantThreads(org, tab, open)
    const threadQuery = useAssistantThread(org, open && threadUid ? threadUid : undefined)
    const thread = threadQuery.data
    useAssistantThreadLive(thread, threadQuery.refetch)

    useEffect(() => {
        // A thread gone (closed elsewhere, another person's): start afresh.
        if (threadQuery.isError) setThreadUid(null)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [threadQuery.isError])

    useEffect(() => {
        bottom.current?.scrollIntoView?.({ block: 'end' })
    }, [thread?.messages.length, thread?.live_turn?.text])

    const messages: Message[] = thread?.messages ?? []
    const closed = !!thread?.closed_at
    const busy = turnInProgress(messages)
    const capped = spendCapped(threads.data?.assistant)
    const contextLabel = context.entity_type
        ? t('context_chip', { type: t(`entity_types.${context.entity_type}`) })
        : t('context_page')

    const send = async () => {
        const body = text.trim()
        if (!body || sending) return
        setSending(true)
        setError(undefined)
        let uid = threadUid
        if (!uid || closed) {
            const created = await createThread(org)
            if (created.error || !created.thread) {
                setError(created.error)
                setSending(false)
                return
            }
            uid = created.thread.uid
            setThreadUid(uid)
        }
        const res = await postMessage(org, uid, body, context)
        setSending(false)
        if (res.error) {
            setError(res.error)
            return
        }
        setText('')
        await invalidate()
    }

    const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            void send()
        }
    }

    const undo = async (m: Message) => {
        setUndoing(true)
        const res = await undoAnswer(org, m.uid)
        setUndoing(false)
        if (res.error) setError(res.error)
        await invalidate()
    }

    const doClose = async () => {
        if (!threadUid) return
        const err = await closeThread(org, threadUid)
        if (err) setError(err)
        setThreadUid(null)
        await invalidate()
    }

    const runError = thread?.run?.error ?? null

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent side="right" className="w-full gap-0 p-0 sm:max-w-md" data-testid="assistant-panel">
                <SheetHeader className="border-b px-4 py-3 pr-12">
                    <SheetTitle className="truncate text-base" data-testid="assistant-thread-title">
                        {thread?.title || t('title')}
                    </SheetTitle>
                    <SheetDescription className="sr-only">{t('empty')}</SheetDescription>
                    <div className="flex items-center gap-1">
                        <Button variant="ghost" size="icon" aria-label={t('threads')} title={t('threads')}
                            onClick={() => setShowList((v) => !v)} data-testid="assistant-thread-list-toggle">
                            <AppIcon path={mdiFormatListBulleted} size="sm" />
                        </Button>
                        <Button variant="ghost" size="icon" aria-label={t('new_thread')} title={t('new_thread')}
                            onClick={() => { setThreadUid(null); setShowList(false) }} data-testid="assistant-new-thread">
                            <AppIcon path={mdiPlus} size="sm" />
                        </Button>
                        {threadUid && !closed && (
                            <Button variant="ghost" size="icon" aria-label={t('close_thread')} title={t('close_thread')}
                                onClick={() => setConfirmClose(true)} data-testid="assistant-close-thread">
                                <AppIcon path={mdiArchiveOutline} size="sm" />
                            </Button>
                        )}
                    </div>
                </SheetHeader>

                {showList ? (
                    <div className="flex-1 overflow-y-auto p-3" data-testid="assistant-thread-list">
                        <div className="mb-2 flex gap-1">
                            {(['open', 'closed'] as const).map((s) => (
                                <Button key={s} size="sm" variant={tab === s ? 'secondary' : 'ghost'} onClick={() => setTab(s)}
                                    data-testid={`assistant-thread-tab-${s}`}>
                                    {t(`tabs.${s}`)}
                                </Button>
                            ))}
                        </div>
                        {(threads.data?.data ?? []).length === 0 && (
                            <p className="text-sm text-muted-foreground">{t('no_threads')}</p>
                        )}
                        <ul className="space-y-1">
                            {(threads.data?.data ?? []).map((th) => (
                                <li key={th.uid}>
                                    <button type="button"
                                        className="w-full truncate rounded-md px-2 py-1.5 text-left text-sm hover:bg-accent"
                                        onClick={() => { setThreadUid(th.uid); setShowList(false) }}
                                        data-testid={`assistant-thread-row-${th.uid}`}>
                                        {th.title || t('untitled')}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                ) : (
                    <div className="flex-1 space-y-3 overflow-y-auto p-4" data-testid="assistant-messages">
                        {messages.length === 0 && <p className="text-sm text-muted-foreground">{t('empty')}</p>}
                        {messages.map((m) => (
                            <AssistantMessage key={m.uid} org={org} message={m} runError={runError} canOpenRun={canOpenRun}
                                onUndo={undo} undoBusy={undoing} />
                        ))}
                        {thread?.live_turn && (
                            <AssistantLiveTurn text={thread.live_turn.text} runStatus={thread.live_turn.run_status} />
                        )}
                        {closed && <p className="text-xs text-muted-foreground">{t('closed_notice')}</p>}
                        <div ref={bottom} />
                    </div>
                )}

                <div className="space-y-2 border-t p-3">
                    <AssistantErrorNotice error={error} capped={capped && !error} onOpenThread={(uid) => setThreadUid(uid)} />
                    <span className="inline-flex rounded-full border px-2 py-0.5 text-xs text-muted-foreground"
                        data-testid="assistant-context">
                        {contextLabel}
                    </span>
                    <div className="flex items-end gap-2">
                        <Textarea value={text} onChange={(e) => setText(e.target.value)} onKeyDown={onKeyDown} rows={2}
                            placeholder={t('input_placeholder')} className="min-h-[2.5rem] flex-1 resize-none"
                            maxLength={8000} data-testid="assistant-input" />
                        <Button size="icon" aria-label={t('send')} title={t('send')} disabled={!text.trim() || sending}
                            onClick={() => void send()} data-testid="assistant-send">
                            <AppIcon path={mdiSend} size="sm" />
                        </Button>
                    </div>
                    {busy && <span className="sr-only">{t('working')}</span>}
                </div>

                <AlertDialog open={confirmClose} onOpenChange={setConfirmClose}>
                    <AlertDialogContent data-testid="assistant-close-dialog">
                        <AlertDialogHeader>
                            <AlertDialogTitle>{t('close_confirm_title')}</AlertDialogTitle>
                            <AlertDialogDescription>{t('close_confirm_description')}</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel data-testid="assistant-close-cancel">{t('cancel')}</AlertDialogCancel>
                            <AlertDialogAction onClick={() => void doClose()} data-testid="assistant-close-confirm">
                                {t('close_confirm_action')}
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </SheetContent>
        </Sheet>
    )
}
